<script setup lang="ts">
import { onUnmounted } from "vue";
import {
  useVisible$,
  Timeline,
  TWEEN,
  type EasingFunction,
  DrawContext
} from "@zyzgroup/core-web";
import Button from "../form/Button.vue";
import Box from "../base/Box.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Button, Box },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    easeName: string;
    easeFunction: EasingFunction;
    canvasWidth?: number;
    canvasHeight?: number;
    graphHorizontalGap?: number;
    graphVerticalGap?: number;
    easeDuration?: number;
    isSelected?: boolean;
  }>(),
  {
    canvasWidth: 180,
    canvasHeight: 100,
    graphHorizontalGap: 5,
    graphVerticalGap: 20,
    easeDuration: 2000,
    isSelected: false
  }
);

const emits = defineEmits<{
  (event: "mount", easeName: string, easeFunction: EasingFunction): void;
  (event: "start", easeName: string, easeFunction: EasingFunction): void;
  (event: "complete", easeName: string, easeFunction: EasingFunction): void;
  (event: "select", easeName: string, easeFunction: EasingFunction): void;
}>();

let drawContext: DrawContext;
let subscription: any;
let isRunning = false;

const start = (ctx, box) => {
  if (subscription) {
    return;
  }
  drawContext = ctx;
  emits("mount", props.easeName, props.easeFunction);
  subscription = useVisible$(box.el).subscribe((v) => {
    if (!v) {
      return;
    }
    if (isRunning) {
      return;
    }
    forceStart();
  });
};

const forceStart = () => {
  isRunning = true;

  emits("start", props.easeName, props.easeFunction);

  // 背景颜色
  drawContext.ctx.fillStyle = "rgb(250,250,250)";
  drawContext.ctx.fillRect(0, 0, props.canvasWidth, props.canvasHeight);

  drawContext.ctx.lineWidth = 0.5;
  drawContext.ctx.strokeStyle = "rgb(230,230,230)";
  drawContext.ctx.beginPath();
  // 上线
  drawContext.ctx.moveTo(0, props.graphVerticalGap);
  drawContext.ctx.lineTo(props.canvasWidth, props.graphVerticalGap);
  // 下线
  drawContext.ctx.moveTo(0, props.canvasHeight - props.graphVerticalGap);
  drawContext.ctx.lineTo(
    props.canvasWidth,
    props.canvasHeight - props.graphVerticalGap
  );
  drawContext.ctx.closePath();
  drawContext.ctx.stroke();

  drawContext.ctx.lineWidth = 2;
  drawContext.ctx.strokeStyle = "rgb(255,127,127)";

  const position = {
    x: props.graphHorizontalGap,
    y: props.canvasHeight - props.graphVerticalGap
  };
  const position_prev = {
    x: props.graphHorizontalGap,
    y: props.canvasHeight - props.graphVerticalGap
  };

  const tl = new Timeline({ frameDuration: props.easeDuration });
  // x轴匀速变化
  tl.addTween(
    position,
    { x: props.canvasWidth - props.graphHorizontalGap },
    {
      frames: 1,
      delay: 0,
      easeFunction: TWEEN.Easing.Linear.None,
      onUpdate: (d) => {
        position.x = d.x;
      },
      onComplete: () => {}
    }
  );
  // y轴 easeFunction 变化
  tl.addTween(
    position,
    { y: props.graphVerticalGap },
    {
      frames: 1,
      delay: 0,
      easeFunction: props.easeFunction,
      onUpdate: (d) => {
        position.y = d.y;
        drawContext.ctx.beginPath();
        drawContext.ctx.moveTo(position_prev.x, position_prev.y);
        drawContext.ctx.lineTo(position.x, position.y);
        drawContext.ctx.closePath();
        drawContext.ctx.stroke();
        position_prev.x = position.x;
        position_prev.y = position.y;
      },
      onComplete: () => {
        emits("complete", props.easeName, props.easeFunction);
        isRunning = false;
      }
    }
  );
  tl.play();
};

const select = () => {
  emits("select", props.easeName, props.easeFunction);
};

onUnmounted(() => {
  subscription?.unsubscribe();
});

defineExpose({ start: forceStart });
</script>

<template>
  <div class="ease-graph-display">
    <div>缓动：{{ easeName }}</div>
    <div>
      <div>
        <Button
          stylePattern="outline"
          height="32px"
          padding="10px"
          @click="forceStart"
        >
          开始
        </Button>
      </div>
      <div>
        <Button
          :stylePattern="props.isSelected ? 'fullfill' : 'outline'"
          height="32px"
          padding="10px"
          @click="select"
        >
          选择
        </Button>
      </div>
    </div>
    <div
      :style="{
        width: canvasWidth + 'px',
        height: canvasHeight + 'px',
        backgroundColor: '#ffffff'
      }"
    >
      <Box
        type="canvas"
        @start="start"
      ></Box>
    </div>
  </div>
</template>

<!--
  ce 中不要使用v-bind
  color: v-bind(fontColor);
-->
<style scoped>
.ease-graph-display {
  display: inline-block;
}
.ease-graph-display > div:nth-child(1) {
  margin-top: 10px;
  font-size: 14px;
}
.ease-graph-display > div:nth-child(2) {
  margin-bottom: 10px;
}
.ease-graph-display > div:nth-child(2) > div {
  display: inline-block;
}
.ease-graph-display > div:nth-child(2) > div + div {
  margin-left: 10px;
}
</style>
